import { useState, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import type { UserRole } from './types/gateway';
import type { CorrectionFormData } from './components/training';
import { useAuth } from './hooks/useAuth';
import { useGateway } from './hooks/useGateway';
import { useSessions } from './hooks/useSessions';
import { useMessages } from './hooks/useMessages';
import { useKnowledge } from './hooks/useKnowledge';
import { useThreads } from './hooks/useThreads';
import { TopBar } from './components/TopBar';
import { Sidebar } from './components/Sidebar';
import { ChatArea } from './components/ChatArea';
import { InputBar } from './components/InputBar';
import { getRoleConfig } from './config/roles';
import TrainingPage from './pages/TrainingPage';
import LoginPage from './pages/LoginPage';
import styles from './App.module.css';

const ROLE_STORAGE_KEY = 'foreman-active-role';
const VALID_ROLES: UserRole[] = ['field', 'designer', 'accounting', 'admin'];

function readStoredRole(): UserRole | null {
  try {
    const raw = localStorage.getItem(ROLE_STORAGE_KEY);
    if (raw && (VALID_ROLES as string[]).includes(raw)) return raw as UserRole;
  } catch {
    /* ignore */
  }
  return null;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

function AuthenticatedApp() {
  const { user } = useAuth();

  // Default role priority: persisted choice > user.uiRole > 'field' (most common contractor case)
  const [role, setRole] = useState<UserRole>(() => {
    const stored = readStoredRole();
    if (stored && stored !== 'admin') return stored;
    const userRole = user?.uiRole;
    if (userRole && userRole !== 'admin') return userRole;
    return 'field';
  });

  // Persist role across sessions
  useEffect(() => {
    try {
      localStorage.setItem(ROLE_STORAGE_KEY, role);
    } catch {
      /* ignore */
    }
  }, [role]);

  const [sidebarTab, setSidebarTab] = useState<'threads' | 'knowledge'>('threads');

  // Gateway connection
  const { status, send } = useGateway();

  // Session management (real OpenClaw sessions)
  const { sessions, activeSessionId, createSession, switchSession, loadingHistory, chatHistory } = useSessions();

  // Threads derived from real sessions
  const { threads, activeThreadId, setActiveThreadId } = useThreads(sessions, activeSessionId);

  // Mobile sidebar toggle
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Select thread = switch session
  const handleSelectThread = useCallback(
    (id: string) => {
      setActiveThreadId(id);
      switchSession(id);
      if (isMobile) {
        setSidebarOpen(false);
      }
    },
    [isMobile, setActiveThreadId, switchSession]
  );

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Knowledge / learning
  const { entries: knowledgeEntries, searchKnowledge, teachMode, toggleTeachMode, submitCorrection } = useKnowledge({ send, activeSessionId });

  // Messages (with history from sessions + live events). Pass role so the
  // hook can attach role context to each chat.send.
  const { messages, isStreaming, sendMessage } = useMessages({
    activeSessionId,
    teachMode,
    chatHistory,
    loadingHistory,
    role,
  });

  const handleSend = useCallback(
    (content: string, files?: File[]) => {
      sendMessage(content, files);
    },
    [sendMessage]
  );

  const handleQuickAction = useCallback(
    (prompt: string) => {
      sendMessage(prompt);
    },
    [sendMessage]
  );

  const handleCorrect = useCallback(
    (messageId: string, data: CorrectionFormData) => {
      submitCorrection(messageId, data.correctedValue, data.category);
    },
    [submitCorrection]
  );

  const handleAction = useCallback(
    (action: string) => {
      sendMessage(action);
    },
    [sendMessage]
  );

  const cfg = getRoleConfig(role);

  const chatView = (
    <>
      <div className={styles.main}>
        <Sidebar
          threads={threads}
          activeThreadId={activeThreadId}
          onSelectThread={handleSelectThread}
          currentRole={role}
          sidebarTab={sidebarTab}
          onTabChange={setSidebarTab}
          knowledgeEntries={knowledgeEntries}
          onSearchKnowledge={searchKnowledge}
          sidebarOpen={sidebarOpen}
        />
        <div className={styles.chat}>
          <div className={styles.blueprintBg} aria-hidden />
          <ChatArea
            messages={messages}
            isStreaming={isStreaming}
            role={role}
            userName={user?.name}
            onCorrect={handleCorrect}
            onAction={handleAction}
            onQuickAction={handleQuickAction}
          />
          <InputBar
            onSend={handleSend}
            teachMode={teachMode}
            onToggleTeachMode={toggleTeachMode}
            disabled={status !== 'connected'}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.app} data-accent={cfg.accent}>
      <TopBar
        connectionStatus={status}
        currentRole={role}
        onRoleChange={setRole}
        sessions={sessions}
        activeSessionId={activeSessionId}
        onCreateSession={createSession}
        onSwitchSession={switchSession}
        onToggleSidebar={toggleSidebar}
      />
      <Routes>
        <Route path="/" element={chatView} />
        <Route path="/training" element={<TrainingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className={styles.app} style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--muted)', fontSize: 14 }}>Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AuthenticatedApp />;
}
